import React, { useCallback, useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { TrendingUp } from "lucide-react";
import { listHistory, deleteHistory } from "@/lib/api";
import { toast } from "sonner";
import { log } from "@/lib/log";
import HistoryItem from "@/components/HistoryItem";

export default function HistoryDrawer({ open, onOpenChange, onLoad, onChanged, onOpenTrends }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listHistory();
      setItems(data.items || []);
    } catch (err) {
      log.error("Failed to load history", err);
      toast.error("Could not load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const remove = async (id) => {
    try {
      await deleteHistory(id);
      toast.success("Deleted");
    } catch (err) {
      log.error("Failed to delete history item", err);
      toast.error("Delete failed");
      return;
    }
    refresh();
    onChanged?.();
  };

  const onSaved = () => {
    refresh();
    onChanged?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="history-drawer">
        <SheetHeader>
          <SheetTitle>Calculation History</SheetTitle>
          <SheetDescription>Log post-op refraction to refine your surgeon factor.</SheetDescription>
        </SheetHeader>
        <Button size="sm" variant="outline" className="mt-4 w-full" onClick={onOpenTrends} data-testid="history-open-trends-btn">
          <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Outcome trend chart
        </Button>
        <div className="mt-6 space-y-3">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-slate-500 rounded-md border border-dashed border-slate-300 p-6 text-center">
              No calculations yet. Run one and it will appear here.
            </div>
          )}
          {items.map((it) => (
            <HistoryItem key={it.id} item={it} onLoad={onLoad} onDelete={remove} onSaved={onSaved} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
